import { Chart } from "chart.js";
import jsPDF from "jspdf";
import autoTable, { Color } from "jspdf-autotable";
import { isNumber, orderBy, round } from "lodash";

import { earningSourceNameToDisplayName } from "services/valuation/constants";
import { REGULAR_CHART_COLOR_LIST } from "theme/constants";

import royaltyExchangeLogo from "./royalty-exchange-logo.png";
import { Currency } from "./types";
import {
  AdministrationReportSources,
  EarningSource,
  JsPDFFileDownload,
  TopTrack,
  TrackEarning,
} from "./types";

const PAGE_LEFT_MARGIN = 10;
const PAGE_ELEMENT_VERTICAL_SPACING = 7;
const FILL_COLOR = [78, 58, 202] as Color;
const SUMMARY_TABLE_WIDTH = 80;

// Note formatting helpers below are duplicated from vp so that the PDF matches. Do not use these helpers outside of the PDF generator.
const formatMoney = (
  currency: keyof Currency,
  amount: string | number | null,
  decimalPlaces?: number,
  options?: Intl.NumberFormatOptions,
) => {
  if (amount === null) {
    return "-";
  }
  const parsedAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return isFinite(parsedAmount)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        currencyDisplay: currency === "ETH" ? "name" : "symbol",
        minimumFractionDigits: decimalPlaces || currency === "ETH" ? 3 : 2,
        maximumFractionDigits: decimalPlaces || currency === "ETH" ? 3 : 2,
        ...options,
      }).format(parsedAmount)
    : "-";
};
const formatUsd = formatMoney.bind(null, "USD");

function formatPercent(value?: number, isDecimal = true) {
  return !!value
    ? new Intl.NumberFormat("en-US", {
        style: "percent",
      }).format(isDecimal ? value : value / 100)
    : "-";
}

const setTopTracks = (doc: jsPDF, tracks: TopTrack[], startY: number) => {
  doc.setFontSize(18);
  doc.text("Top Tracks", PAGE_LEFT_MARGIN, startY);

  const body = tracks.map(track => ({
    track: track.track,
    total: formatUsd(track.total),
  }));

  autoTable(doc, {
    body: body,
    columns: [
      { dataKey: "track", header: "Track" },
      { dataKey: "total", header: "Total" },
    ],
    startY: startY + 5,
    columnStyles: { 1: { halign: "right" } },
    headStyles: {
      halign: "center",
      fillColor: FILL_COLOR,
    },
    tableWidth: SUMMARY_TABLE_WIDTH,
  });
};

const setTopSources = (doc: jsPDF, tracks: EarningSource[], startY: number) => {
  doc.setFontSize(18);
  doc.text("Top Sources", PAGE_LEFT_MARGIN, startY);

  const body = tracks.map(earningTrack => ({
    source:
      earningSourceNameToDisplayName[earningTrack.source] ||
      earningTrack.source,
    total: formatUsd(earningTrack.total),
  }));

  autoTable(doc, {
    body: body,
    columns: [
      { dataKey: "source", header: "Source" },
      { dataKey: "total", header: "Total" },
    ],
    startY: startY + 5,
    columnStyles: {
      1: { halign: "right" },
    },

    headStyles: {
      valign: "middle",
      halign: "center",
      fillColor: FILL_COLOR,
    },
    tableWidth: SUMMARY_TABLE_WIDTH,
  });
};

const tracksToBodyRows = (tracks: TrackEarning[]) => {
  const body = tracks.map(track => ({
    track_id: track.track_id,
    track_title: track.track_title,
    split_percent: formatPercent(track.split_percent / 100),
    music_user: track.music_user,
    payable_amount: formatUsd(track.payable_amount),
    number_of_plays: track.number_of_plays,
  }));
  return body;
};

const setEarningSources = (doc: jsPDF, earning_source: EarningSource) => {
  const finalY = !(doc as any).autoTable.previous
    ? 0
    : ((doc as any).autoTable.previous.finalY as number);

  const startY = finalY + PAGE_ELEMENT_VERTICAL_SPACING * 4; // *4 to add more margin between the summary and line item tables
  const formattedSource =
    earningSourceNameToDisplayName[earning_source.source] ||
    earning_source.source;

  // Tracks by Sources
  doc.text(formattedSource, PAGE_LEFT_MARGIN, startY);

  autoTable(doc, {
    body: tracksToBodyRows(earning_source.earnings_rows),
    columns: [
      { dataKey: "track_id", header: "Track Id" },
      { dataKey: "track_title", header: "Title" },
      { dataKey: "split_percent", header: "Split %" },
      { dataKey: "music_user", header: "Music User" },
      { dataKey: "number_of_plays", header: "Number of Plays" },
      { dataKey: "payable_amount", header: "Payable" },
    ],

    startY: startY + PAGE_ELEMENT_VERTICAL_SPACING,
    showHead: "firstPage",
    columnStyles: { 4: { halign: "right" }, 5: { halign: "right" } },
    headStyles: { fillColor: FILL_COLOR },
  });
};

const setHeader = (
  doc: jsPDF,
  report: AdministrationReportSources,
  startX: number,
  startY: number,
) => {
  doc.setFontSize(18);
  doc.text(report.distribution_name, PAGE_LEFT_MARGIN, startY);
  doc.setFontSize(12);

  doc.text(
    report.original_seller_name
      ? `Original Seller Name: ${report.original_seller_name}`
      : "",
    PAGE_LEFT_MARGIN,
    startY + 7,
  );

  report.payee &&
    doc.text(
      `Name: ${report.payee.name}\n
    `,
      PAGE_LEFT_MARGIN,
      23,
    );
  doc.addImage(
    royaltyExchangeLogo,
    "PNG",
    startX,
    5,
    68,
    19,
    undefined,
    "FAST",
    0,
  );
};

export const calculatePercentRecouped = (
  currentAdvancedBalance: number | null,
  purchaseReturnAmount: number | null,
): number =>
  isNumber(currentAdvancedBalance) && isNumber(purchaseReturnAmount)
    ? round(
        ((currentAdvancedBalance + purchaseReturnAmount) /
          purchaseReturnAmount) *
          100,
        2,
      )
    : 0;
const setSummaryInfo = (
  doc: jsPDF,
  report: AdministrationReportSources,
  startY: number,
  purchasedTerm: string | null,
  ownerIsBuyer: boolean,
) => {
  doc.setFontSize(12);
  const lineHeight = 7;
  const sectionRightMargin = 95;
  let yOffset = startY;
  if (purchasedTerm === "fixed_return" && ownerIsBuyer) {
    doc.text("Prior Advance Balance:", PAGE_LEFT_MARGIN, yOffset);
    doc.text(
      formatUsd(report.prior_advance_balance, undefined, {
        currencySign: "accounting",
      }),
      sectionRightMargin,
      yOffset,
      {
        align: "right",
      },
    );
    yOffset += lineHeight;

    doc.text("Payment Amount:", PAGE_LEFT_MARGIN, yOffset);
    doc.text(formatUsd(report.net_payable), sectionRightMargin, yOffset, {
      align: "right",
    });
    yOffset += lineHeight;

    doc.text("Current Advance Balance:", PAGE_LEFT_MARGIN, yOffset);
    doc.text(
      formatUsd(report.current_advance_balance, undefined, {
        currencySign: "accounting",
      }),
      sectionRightMargin,
      yOffset,
      {
        align: "right",
      },
    );

    const recouped = calculatePercentRecouped(
      report.current_advance_balance,
      report.purchased_term_return_amount,
    );

    doc.text(`Recouped: ${recouped}%`, 190, yOffset, { align: "right" });
  } else {
    doc.text("Gross:", PAGE_LEFT_MARGIN, yOffset);
    doc.text(formatUsd(report.gross_payable), sectionRightMargin, yOffset, {
      align: "right",
    });
    yOffset += lineHeight;

    doc.text("Adjustments:", PAGE_LEFT_MARGIN, yOffset);
    doc.text(formatUsd(report.adjustments), sectionRightMargin, yOffset, {
      align: "right",
    });
    yOffset += lineHeight;

    doc.text("Net Payable:", PAGE_LEFT_MARGIN, yOffset);
    doc.text(formatUsd(report.net_payable), sectionRightMargin, yOffset, {
      align: "right",
    });
  }
};

const generateChart = (
  doc: jsPDF,
  title: string,
  labels: string[],
  data: number[],
  startY: number,
) => {
  const chart_data = {
    type: "doughnut",
    data: {
      labels: labels,
      datasets: [
        {
          label: title,
          data: data,
          backgroundColor: REGULAR_CHART_COLOR_LIST,
          borderColor: ["#7F8689"],
        },
      ],
    },
    options: {
      animation: {
        // this is here to force the chart to render synchronously
        duration: 0,
      },
    },
  };

  const canvas = document.getElementById(
    "pdf-report-canvas",
  ) as HTMLCanvasElement;

  const context = (canvas as any).getContext("2d");

  Chart.defaults.global.defaultFontSize = 15;
  new Chart(context, chart_data);

  const canvasImg = canvas.toDataURL("image/PNG", 1.0);
  doc.addImage(canvasImg, "PNG", 100, startY, 90, 60);
};

export const generateAssetDistributionPDF = (
  assetDistributionReport: any | null,
  purchasedTerm: string | null,
  ownerIsBuyer: boolean,
): JsPDFFileDownload => {
  const doc = new jsPDF();
  doc.setLineHeightFactor(1);
  doc.setFont("helvetica");

  // default page size is 216 x 279 mm
  if (!assetDistributionReport) {
    return { fileName: "", pdfFile: doc };
  }

  const sortedTopTracks = orderBy(
    assetDistributionReport.top_tracks,
    ["total"],
    ["desc"],
  );
  const sortedEarningSources = orderBy(
    assetDistributionReport.earning_sources,
    ["total"],
    ["desc"],
  );

  if (sortedTopTracks.length > 1) {
    generateChart(
      doc,
      "",
      sortedTopTracks.map(track => track.track),
      sortedTopTracks.map(track => track.total),
      54,
    );
  }

  if (sortedEarningSources.length > 1) {
    generateChart(
      doc,
      "",
      sortedEarningSources.map(
        earningTrack =>
          earningSourceNameToDisplayName[earningTrack.source] ||
          earningTrack.source,
      ),
      sortedEarningSources.map(track => track.total),
      115,
    );
  }

  let yOffset = 10;
  setHeader(doc, assetDistributionReport, 130, yOffset);
  yOffset += 26;

  setSummaryInfo(
    doc,
    assetDistributionReport,
    yOffset,
    purchasedTerm,
    ownerIsBuyer,
  );
  yOffset += 28;

  setTopTracks(doc, sortedTopTracks, yOffset);
  yOffset += 65;

  setTopSources(doc, sortedEarningSources, yOffset);

  assetDistributionReport?.earning_sources.map((source: any) => {
    setEarningSources(doc, source);
  });

  return { fileName: assetDistributionReport.file_name, pdfFile: doc };
};
